/**
 * DrilldownCallList.jsx — TM 요약 드릴다운 콜 목록 컴포넌트
 *
 * 결과 유형(feedback) 클릭 시 해당 기간의 콜 상세 목록을 표시합니다.
 * - 모바일: 카드형 목록
 * - 데스크탑: 테이블형 목록
 * dark mode: CSS 변수 토큰 사용
 *
 * Props:
 *   feedback  {string}  — 조회할 결과 유형 (예: "계약", "다음에", ...)
 *   dateRange {object}  — { start: 'YYYY-MM-DD', end: 'YYYY-MM-DD' }
 *   onClose   {function} — 드릴다운 카드 닫기 핸들러
 */
import React, { useEffect, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { useToast } from '../../../components/Toast';
import { useIsMobile, fmtDate, TH, TD } from './utils';

function defaultBuildCallsPath({ feedback, dateRange }) {
  const params = new URLSearchParams();
  params.set('feedback', feedback);
  if (dateRange.start) params.set('start_date', dateRange.start);
  if (dateRange.end)   params.set('end_date',   dateRange.end);
  return `/api/tm/calls?${params.toString()}`;
}

export default function DrilldownCallList({
  feedback,
  dateRange,
  year,
  month,
  buildCallsPath = defaultBuildCallsPath,
  onClose,
}) {
  const toast    = useToast();
  const isMobile = useIsMobile();
  const [calls, setCalls]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const path = buildCallsPath({ feedback, dateRange, year, month });

    apiClient
      .get(path)
      .then((res) => {
        if (cancelled) return;
        if (res.data?.success) {
          let data = res.data.data || [];
          // 날짜 필터: API 응답 중 dateRange 범위를 벗어난 항목 클라이언트에서 재필터링
          if (dateRange.start) data = data.filter((r) => r.call_date >= dateRange.start);
          if (dateRange.end)   data = data.filter((r) => r.call_date <= dateRange.end);
          setCalls(data);
        } else {
          toast.error(res.data?.message || '콜 목록을 불러오지 못했습니다.');
        }
      })
      .catch((err) => {
        if (!cancelled) toast.error(err.response?.data?.message || '콜 목록 조회 실패');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [buildCallsPath, feedback, dateRange, month, toast, year]);

  return (
    <div>
      {/* ── 드릴다운 헤더: 결과 유형명 + 건수 + 닫기 버튼 ── */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
          결과 ·{' '}
          <span style={{ color: '#2563eb' }}>{feedback}</span>
          {!loading && (
            <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6 }}>
              {calls.length}건
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          style={{
            marginLeft: 'auto', padding: '4px 12px', fontSize: 12,
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 6, cursor: 'pointer', color: 'var(--text-muted)', fontWeight: 500,
          }}
        >
          닫기
        </button>
      </div>

      {/* ── 로딩 상태 ── */}
      {loading && (
        <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
          불러오는 중...
        </div>
      )}

      {/* ── 빈 결과 ── */}
      {!loading && calls.length === 0 && (
        <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
          해당 결과의 콜 데이터가 없습니다.
        </div>
      )}

      {/* ── 모바일: 카드형 목록 ── */}
      {!loading && calls.length > 0 && isMobile && (
        <div style={{ display: 'grid', gap: 8 }}>
          {calls.map((row) => (
            <div key={row.id} style={{
              border: '1px solid var(--border)', borderRadius: 9,
              padding: '10px 12px', background: 'var(--bg-card)',
            }}>
              {/* 1행: 결과칩 + 콜일자 + 방문일자 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
                {row.feedback ? (
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                    fontSize: 11, fontWeight: 600, color: '#fff',
                    background: row.is_contracted ? '#10b981' : '#6366f1', whiteSpace: 'nowrap',
                  }}>
                    {row.feedback}
                  </span>
                ) : null}
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmtDate(row.call_date)}</span>
                {row.visit_date && (
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>방문 {fmtDate(row.visit_date)}</span>
                )}
              </div>
              {/* 2행: 상호 */}
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
                {row.company_name || '-'}
              </div>
              {/* 3행: 주소 */}
              {row.address && (
                <div style={{ fontSize: 12, color: 'var(--text-sub)', marginBottom: 3, wordBreak: 'break-all' }}>
                  {row.address}
                </div>
              )}
              {/* 4행: 전화번호·휴대폰 */}
              {(row.phone || row.mobile_phone) && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 3 }}>
                  {[row.phone, row.mobile_phone].filter(Boolean).join(' · ')}
                </div>
              )}
              {/* 5행: 메모 */}
              {row.feedback_memo && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  {row.feedback_memo}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── 데스크탑: 테이블형 목록 ── */}
      {!loading && calls.length > 0 && !isMobile && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
            <thead>
              <tr>
                <th style={TH}>콜일자</th>
                <th style={TH}>방문일자</th>
                <th style={TH}>상호</th>
                <th style={TH}>주소</th>
                <th style={TH}>전화번호</th>
                <th style={TH}>휴대폰</th>
                <th style={TH}>결과</th>
                <th style={TH}>메모</th>
              </tr>
            </thead>
            <tbody>
              {calls.map((row) => (
                <tr key={row.id}>
                  <td style={TD}>{fmtDate(row.call_date)}</td>
                  <td style={TD}>{fmtDate(row.visit_date)}</td>
                  <td style={{ ...TD, maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {row.company_name || '-'}
                  </td>
                  <td style={{ ...TD, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {row.address || '-'}
                  </td>
                  <td style={TD}>{row.phone || '-'}</td>
                  <td style={TD}>{row.mobile_phone || '-'}</td>
                  <td style={TD}>
                    {row.feedback ? (
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                        fontSize: 11, fontWeight: 600, color: '#fff',
                        background: row.is_contracted ? '#10b981' : '#6366f1', whiteSpace: 'nowrap',
                      }}>
                        {row.feedback}
                      </span>
                    ) : '-'}
                  </td>
                  <td style={{ ...TD, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--text-muted)' }}>
                    {row.feedback_memo || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
